import React, { useState, useRef } from 'react';
import Avatar from 'react-avatar-edit';
import axios from 'axios';
import { Button } from 'antd';
import { UploadOutlined, PictureOutlined } from '@ant-design/icons';

import { useTranslation } from 'react-i18next';

const MAX_BYTES = 5000000;

// Phones and tablets cannot drag a file onto the page, so the drop area
// only offers to choose one there
const canDrag = () => {
  try {
    return !window.matchMedia('(hover: none), (pointer: coarse)').matches;
  } catch (e) {
    return true;
  }
};

// `hasPhoto`: false while the account still shows the default avatar, so the
// modal opens straight onto the file picker instead of cropping the default
const AvatarComponent = ({ closeModal, username, avatar, hasPhoto, rerender, onError }) => {
  const [t] = useTranslation();
  // The image in the cropper: the current photo, a newly picked one, or none
  // (react-avatar-edit then shows its own drop/select area)
  const [src, setSrc] = useState(hasPhoto ? avatar : null);
  // Only a file the person picked in this modal may be uploaded; cropping the
  // current photo again would just re-upload it
  const [picked, setPicked] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewFileName, setPreviewFileName] = useState(null);
  const fileInput = useRef(null);

  const token = localStorage.getItem('auth-token');
  const config = {
    headers: {'auth-token': token}
  }

  // The cropper's own close icon clears the image back to its drop area
  const onClose = () => {
    setPreview(null);
    setPicked(false);
    setSrc(null);
  }

  const onCrop = (preview) => {
    setPreview(preview);
  }

  const tooBig = (file) => {
    if (file.size <= MAX_BYTES) return false;
    if (onError) onError(t('inputErrors.fileIsTooBig'));
    return true;
  }

  // A file chosen in react-avatar-edit's own drop area
  const onBeforeFileLoad = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (tooBig(file)) {
      e.target.value = "";
    } else {
      setPreviewFileName(file.name);
    }
  }

  const onFileLoad = () => setPicked(true);

  // A file chosen with the "Choose another photo" button: handed to the
  // cropper as its new source
  const chooseFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file || tooBig(file)) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(null);
      setPreviewFileName(file.name);
      setSrc(reader.result);
      setPicked(true);
    };
    reader.readAsDataURL(file);
  }

// Create Blob using the base64 string which is returned from cropping the avatar
const  b64toBlob = (dataURI) => {

  var byteString = atob(dataURI.split(',')[1]);
  var ab = new ArrayBuffer(byteString.length);
  var ia = new Uint8Array(ab);

  for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: 'image/jpeg' });
}

const submitAvatar = () => {
  // Nothing cropped yet means nothing to upload
  if (!preview) return;
  var newBlob = b64toBlob(preview);

  // Create FormData object using Blob. This will be sent to the backend
  var newFD = new FormData();
  newFD.append('userAvatar', newBlob, previewFileName);

  setUploading(true);
  axios.patch(`/users/${username}/avatar`, newFD, config)
  .then(res => {
    rerender(preview);
    closeModal();
  })
  .catch(err => {
    setUploading(false);
    if (onError) onError();
  })
}

  return (
    <>
      <div className="modal-avatar-container">
        <div className="edit-avatar-container">
          {/* Remounted per source: the library only reads src when it mounts */}
          <Avatar
            key={src ? src.slice(-32) : 'empty'}
            width={250}
            height={250}
            onCrop={onCrop}
            onClose={onClose}
            src={src || undefined}
            onBeforeFileLoad={onBeforeFileLoad}
            onFileLoad={onFileLoad}
            mimeTypes="image/jpeg,image/png,image/webp"
            label={t(canDrag() ? 'dash.photoDrop' : 'dash.photoDropTouch')}
            labelStyle={{ color: 'rgba(242, 237, 227, 0.72)', fontSize: 14, fontWeight: 700, cursor: 'pointer', lineHeight: '250px', display: 'block', textAlign: 'center' }}
            borderStyle={{ border: '2px dashed rgba(242, 237, 227, 0.24)', borderRadius: 12, textAlign: 'center' }}
          />
        </div>
      </div>
      <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" onChange={chooseFile} hidden />
      {src && <p className="avatar-hint">{picked ? t('dash.photoCropHint') : t('dash.photoCurrent')}</p>}
      {src && <Button block icon={<PictureOutlined />} onClick={() => fileInput.current.click()} className="avatar-choose">{t('dash.photoChoose')}</Button>}
      <div className="avatar-actions">
        <Button onClick={closeModal}>{t('dash.cancel')}</Button>
        <Button type="primary" icon={<UploadOutlined />} disabled={!picked || !preview} loading={uploading} onClick={submitAvatar}>{t('upload')}</Button>
      </div>
    </>
  )
}

export default AvatarComponent
